/**
 * CharCreateScene — Complete character creation flow with 7 steps.
 *
 * Step 1: Race Selection
 * Step 2: Portrait Selection
 * Step 3: Attribute Allocation
 * Step 4: Skill Tree Selection
 * Step 5: Starting Scenario
 * Step 6: Name Entry
 * Step 7: Summary & Confirm
 *
 * Each step is rendered as a sub-panel within the scene.
 * Full keyboard/gamepad/touch navigation via FocusManager.
 */

import { Container } from 'pixi.js';
import debugLog from '../debug/DebugLog.js';
import sceneManager from '../core/SceneManager.js';
import focusManager from '../input/FocusManager.js';
import dataStore from '../systems/DataLoader.js';
import Character from '../entities/Character.js';
import Party from '../entities/Party.js';
import inventorySystem from '../systems/InventorySystem.js';

/** Portrait color palettes per race. */
const PORTRAIT_COLORS = {
  human:    ['#6a5a4e', '#8b7355', '#a0845c', '#c4a265', '#5c4033', '#7b6853'],
  elf:      ['#3a6b4f', '#5c9b6e', '#4a8a6a', '#6eb88a', '#2d5c43', '#7acba0'],
  dwarf:    ['#7a5533', '#995c2a', '#6b4423', '#b07040', '#8a6035', '#a37545'],
  halfling: ['#6a7b5a', '#8a9b6a', '#7b8c60', '#a0b080', '#5a6c4a', '#90a870'],
  orc:      ['#4a6a3a', '#5c8040', '#3d5c2e', '#6b9048', '#4f7535', '#7aa050'],
};

/** Random name banks per race. */
const NAME_BANKS = {
  human:    ['Aldric', 'Elara', 'Cedric', 'Lyra', 'Rowan', 'Ivy', 'Gareth', 'Sera', 'Marcus', 'Thea', 'Edmund', 'Wren', 'Roland', 'Mira', 'Oswin', 'Freya'],
  elf:      ['Aelindra', 'Thalion', 'Elowen', 'Faelar', 'Sylthari', 'Galathil', 'Ilyana', 'Lorien', 'Nimue', 'Caelum', 'Arwen', 'Feyn', 'Lirien', 'Orin', 'Sylva', 'Theron'],
  dwarf:    ['Thorin', 'Brenna', 'Durin', 'Hilda', 'Grimm', 'Astrid', 'Balin', 'Ingrid', 'Thrain', 'Sigrid', 'Brom', 'Helga', 'Dain', 'Frida', 'Gloin', 'Magna'],
  halfling: ['Pip', 'Rosie', 'Milo', 'Daisy', 'Finn', 'Poppy', 'Bramble', 'Clover', 'Tuck', 'Primrose', 'Wren', 'Sage', 'Brin', 'Tansy', 'Ash', 'Holly'],
  orc:      ['Grak', 'Shara', 'Thok', 'Bruga', 'Karga', 'Naz', 'Vashka', 'Rok', 'Gorla', 'Thrug', 'Azka', 'Morg', 'Draka', 'Urk', 'Zolla', 'Brak'],
};

/** Starting scenario definitions. */
const SCENARIOS = [
  {
    id: 'exiled_knight',
    title: 'The Exiled Knight',
    description: 'A disgraced warrior seeking redemption. Accused of a crime you did not commit, you wander the roads with nothing but your blade and your honor.',
    conditions: 'Start in Greenhollow with basic weapon and light armor. Begin with a lead on the true criminal.',
  },
  {
    id: 'wandering_scholar',
    title: 'The Wandering Scholar',
    description: 'A lore-keeper investigating disturbing reports of demonic activity at the borders. Knowledge is your weapon, and every ruin holds answers.',
    conditions: 'Start in Greenhollow with a tome of lore and a scholar\'s kit. Begin with extra knowledge about the demon threat.',
  },
  {
    id: 'merchants_heir',
    title: 'The Merchant\'s Heir',
    description: 'Your family\'s caravan was destroyed by bandits, and with it your fortune. Now you must rebuild from nothing, turning trade routes into lifelines.',
    conditions: 'Start in Dustwind Plains with extra gold (50g) and a damaged wagon. Begin with knowledge of trade routes.',
  },
  {
    id: 'apprentice',
    title: 'The Apprentice',
    description: 'A young mage sent by your master to investigate corruption spreading through the land. Your spells are few but your potential is vast.',
    conditions: 'Start in Greenhollow with a basic staff and spell scroll. Begin with an arcane focus granting +1 INT.',
  },
  {
    id: 'survivor',
    title: 'The Survivor',
    description: 'The last survivor of a destroyed border town. You saw the demons come. Now you carry the memory of what was lost and the fury to fight back.',
    conditions: 'Start in Moonfen Marsh with survival gear. Begin with knowledge of demon weaknesses and +1 VIT.',
  },
];

const STEP_LABELS = ['Race', 'Portrait', 'Attributes', 'Skills', 'Scenario', 'Name', 'Confirm'];
const TOTAL_STEPS = 7;
const BONUS_POINTS = 10;

export default class CharCreateScene {
  constructor() {
    this.container = new Container();
    this._el = null;
    this._currentStep = 0;

    // Character data being built
    this._selectedRace = null;
    this._selectedPortrait = null;
    this._bonusAttributes = { str: 0, agi: 0, int: 0, vit: 0, wis: 0, cha: 0 };
    this._selectedSkillTree = null;
    this._selectedScenario = null;
    this._characterName = '';

    // Cancel handler for focus manager
    this._cancelHandler = null;
  }

  async init() {
    debugLog.info('CharCreateScene: init');
  }

  async enter() {
    debugLog.event('CharCreateScene: enter');

    // Load data if not loaded
    if (!dataStore.isLoaded) {
      dataStore.loadAll();
    }

    this._createDOM();
    this._renderStep();
  }

  exit() {
    debugLog.event('CharCreateScene: exit');
    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
    }
    this._el = null;
    focusManager.clear();
  }

  // ── DOM Creation ───────────────────────────────────────

  _createDOM() {
    const uiRoot = document.getElementById('ui-root');

    this._el = document.createElement('div');
    this._el.id = 'chargen-scene';
    this._el.className = 'chargen-scene';
    this._el.setAttribute('data-scene', 'CharCreate');

    // Step indicator
    this._stepsEl = document.createElement('div');
    this._stepsEl.className = 'chargen-steps';
    this._el.appendChild(this._stepsEl);

    // Mobile stats panel (collapsible, above content)
    this._statsMobileEl = document.createElement('div');
    this._statsMobileEl.className = 'chargen-stats-mobile';
    this._statsMobileEl.id = 'chargen-stats-mobile';

    const toggleBtn = document.createElement('button');
    toggleBtn.className = 'chargen-stats-toggle';
    toggleBtn.setAttribute('data-focusable', 'true');
    toggleBtn.setAttribute('data-stats-toggle', 'true');

    const summaryLine = document.createElement('span');
    summaryLine.className = 'stats-summary-line';
    summaryLine.textContent = 'Build: Select a race...';
    toggleBtn.appendChild(summaryLine);

    const toggleIcon = document.createElement('span');
    toggleIcon.className = 'stats-toggle-icon';
    toggleIcon.textContent = '\u25BC'; // down arrow
    toggleBtn.appendChild(toggleIcon);

    toggleBtn.addEventListener('click', () => {
      this._statsMobileEl.classList.toggle('expanded');
    });

    this._statsMobileEl.appendChild(toggleBtn);

    const mobilePanel = document.createElement('div');
    mobilePanel.className = 'chargen-stats-panel';
    mobilePanel.id = 'chargen-stats-panel-mobile';
    this._statsMobileEl.appendChild(mobilePanel);

    this._el.appendChild(this._statsMobileEl);

    // Content wrapper (holds content + sidebar)
    const wrapper = document.createElement('div');
    wrapper.className = 'chargen-content-wrapper';

    // Content area
    this._contentEl = document.createElement('div');
    this._contentEl.className = 'chargen-content';
    wrapper.appendChild(this._contentEl);

    // Desktop/tablet stats sidebar
    this._statsSidebarEl = document.createElement('div');
    this._statsSidebarEl.className = 'chargen-stats-sidebar';
    this._statsSidebarEl.id = 'chargen-stats-sidebar';

    const sidebarPanel = document.createElement('div');
    sidebarPanel.className = 'chargen-stats-panel';
    sidebarPanel.id = 'chargen-stats-panel-sidebar';
    this._statsSidebarEl.appendChild(sidebarPanel);

    wrapper.appendChild(this._statsSidebarEl);

    this._el.appendChild(wrapper);

    // Footer with nav buttons
    this._footerEl = document.createElement('div');
    this._footerEl.className = 'chargen-footer';
    this._el.appendChild(this._footerEl);

    // Listen for cancel (B button / Escape via FocusManager)
    this._cancelHandler = () => {
      this._goBack();
    };
    this._el.addEventListener('focusgroup:cancelled', this._cancelHandler);

    uiRoot.appendChild(this._el);
  }

  // ── Step Indicator ─────────────────────────────────────

  _renderStepIndicator() {
    this._stepsEl.innerHTML = '';
    for (let i = 0; i < TOTAL_STEPS; i++) {
      if (i > 0) {
        const connector = document.createElement('div');
        connector.className = 'chargen-step-connector' +
          (i <= this._currentStep ? ' completed' : '');
        this._stepsEl.appendChild(connector);
      }
      const dot = document.createElement('div');
      dot.className = 'chargen-step-dot' +
        (i === this._currentStep ? ' active' : '') +
        (i < this._currentStep ? ' completed' : '');
      dot.title = STEP_LABELS[i];
      this._stepsEl.appendChild(dot);
    }
  }

  // ── Footer Buttons ─────────────────────────────────────

  _renderFooter() {
    this._footerEl.innerHTML = '';

    // Back button
    const backBtn = document.createElement('button');
    backBtn.className = 'btn-cancel';
    backBtn.setAttribute('data-focusable', 'true');
    backBtn.textContent = this._currentStep === 0 ? 'Cancel' : 'Back';
    backBtn.addEventListener('click', () => this._goBack());
    this._footerEl.appendChild(backBtn);

    // Next / Start button
    const nextBtn = document.createElement('button');
    nextBtn.className = 'btn-accept';
    nextBtn.setAttribute('data-focusable', 'true');
    if (this._currentStep === TOTAL_STEPS - 1) {
      nextBtn.textContent = 'Start Adventure';
    } else {
      nextBtn.textContent = 'Next';
    }
    nextBtn.addEventListener('click', () => this._goNext());
    this._footerEl.appendChild(nextBtn);
  }

  // ── Step Rendering ─────────────────────────────────────

  _renderStep(preserveScroll = false) {
    // Preserve scroll position when re-rendering the same step
    const scrollTop = preserveScroll && this._contentEl ? this._contentEl.scrollTop : 0;

    this._renderStepIndicator();
    this._renderFooter();
    this._contentEl.innerHTML = '';

    // Title
    const title = document.createElement('h2');
    title.className = 'chargen-step-title';
    title.textContent = this._getStepTitle();
    this._contentEl.appendChild(title);

    // Body
    const body = document.createElement('div');
    body.className = 'chargen-step-body';
    this._contentEl.appendChild(body);

    switch (this._currentStep) {
      case 0: this._renderRaceStep(body); break;
      case 1: this._renderPortraitStep(body); break;
      case 2: this._renderAttributeStep(body); break;
      case 3: this._renderSkillTreeStep(body); break;
      case 4: this._renderScenarioStep(body); break;
      case 5: this._renderNameStep(body); break;
      case 6: this._renderSummaryStep(body); break;
    }

    // Update stats panel
    this._updateStatsPanel();

    // Restore scroll position
    if (preserveScroll && scrollTop > 0) {
      this._contentEl.scrollTop = scrollTop;
    }

    // Push focus group for navigation
    focusManager.clear();
    focusManager.pushGroup('chargen-step', this._el);

    // Debug: expose focus manager group info
    if (window.__debug) {
      const group = focusManager._currentGroup();
      window.__debug.focusGroupSize = group ? group.elements.length : -1;
      window.__debug.focusManager = focusManager;
    }
  }

  _getStepTitle() {
    const titles = [
      'Choose Your Race',
      'Select a Portrait',
      'Allocate Attributes',
      'Choose Your Path',
      'Choose Your Story',
      'Name Your Character',
      'Review & Confirm',
    ];
    return titles[this._currentStep];
  }

  // ── Step 1: Race Selection ─────────────────────────────

  _renderRaceStep(container) {
    const races = dataStore.getAll('races').filter(r => r.playable);

    const grid = document.createElement('div');
    grid.className = 'race-grid';

    for (const race of races) {
      const card = document.createElement('div');
      card.className = 'race-card' + (this._selectedRace?.id === race.id ? ' selected' : '');
      card.setAttribute('data-focusable', 'true');
      card.setAttribute('data-race-id', race.id);

      // Name
      const nameEl = document.createElement('div');
      nameEl.className = 'race-card-name';
      nameEl.textContent = race.name;
      card.appendChild(nameEl);

      // Description
      const descEl = document.createElement('div');
      descEl.className = 'race-card-desc';
      descEl.textContent = race.description;
      card.appendChild(descEl);

      // Attribute bonuses
      const bonuses = document.createElement('div');
      bonuses.className = 'race-card-bonuses';
      const attrKeys = ['str', 'agi', 'int', 'vit', 'wis', 'cha'];
      for (const key of attrKeys) {
        const val = race.attributeBonuses[key];
        if (val !== 0) {
          const tag = document.createElement('span');
          tag.className = 'race-bonus-tag ' + (val > 0 ? 'positive' : 'negative');
          tag.textContent = `${val > 0 ? '+' : ''}${val} ${key.toUpperCase()}`;
          bonuses.appendChild(tag);
        }
      }
      card.appendChild(bonuses);

      // Traits summary
      const traits = document.createElement('div');
      traits.className = 'race-card-traits';
      traits.innerHTML = race.traits.map(t => `<span>${t.name}</span>`).join(', ');
      card.appendChild(traits);

      card.addEventListener('click', () => {
        this._selectedRace = race;
        // Reset portrait when race changes
        this._selectedPortrait = null;
        // Re-render to update selection state
        this._renderStep(true);
      });

      grid.appendChild(card);
    }

    container.appendChild(grid);

    // Detail panel for selected race
    if (this._selectedRace) {
      const detail = document.createElement('div');
      detail.className = 'race-detail';

      const detailName = document.createElement('div');
      detailName.className = 'race-detail-name';
      detailName.textContent = this._selectedRace.name;
      detail.appendChild(detailName);

      const detailLore = document.createElement('div');
      detailLore.className = 'race-detail-lore';
      detailLore.textContent = this._selectedRace.lore;
      detail.appendChild(detailLore);

      const traitList = document.createElement('div');
      traitList.className = 'race-detail-traits';
      for (const trait of this._selectedRace.traits) {
        const item = document.createElement('div');
        item.className = 'race-trait-item';
        item.innerHTML = `<div class="race-trait-name">${trait.name}</div><div class="race-trait-desc">${trait.description}</div>`;
        traitList.appendChild(item);
      }
      detail.appendChild(traitList);
      container.appendChild(detail);
    }
  }

  // ── Step 2: Portrait Selection ─────────────────────────

  _renderPortraitStep(container) {
    const raceId = this._selectedRace?.id || 'human';
    const colors = PORTRAIT_COLORS[raceId] || PORTRAIT_COLORS.human;
    const initials = (this._selectedRace?.name || 'H').substring(0, 2).toUpperCase();

    const layout = document.createElement('div');
    layout.className = 'portrait-layout';

    // Preview area
    const previewSection = document.createElement('div');
    previewSection.className = 'portrait-layout-preview';

    const preview = document.createElement('div');
    preview.className = 'portrait-preview-large';
    const selectedColor = this._selectedPortrait?.color || colors[0];
    preview.style.background = selectedColor;
    preview.textContent = initials;
    previewSection.appendChild(preview);
    layout.appendChild(previewSection);

    // Grid
    const gridSection = document.createElement('div');
    gridSection.className = 'portrait-layout-grid';

    const grid = document.createElement('div');
    grid.className = 'portrait-grid';

    for (let i = 0; i < colors.length; i++) {
      const option = document.createElement('div');
      option.className = 'portrait-option' +
        (this._selectedPortrait?.index === i ? ' selected' : '');
      option.setAttribute('data-focusable', 'true');
      option.setAttribute('data-portrait-index', String(i));
      option.style.background = colors[i];
      option.textContent = initials;

      option.addEventListener('click', () => {
        this._selectedPortrait = { index: i, color: colors[i], initials };
        this._renderStep(true);
      });

      grid.appendChild(option);
    }

    gridSection.appendChild(grid);
    layout.appendChild(gridSection);
    container.appendChild(layout);

    // Auto-select first if none selected
    if (!this._selectedPortrait) {
      this._selectedPortrait = { index: 0, color: colors[0], initials };
    }
  }

  // ── Step 3: Attribute Allocation ───────────────────────

  _renderAttributeStep(container) {
    const race = this._selectedRace || dataStore.getById('races', 'human');
    const attrs = dataStore.getAll('attributes');
    const remaining = this._getRemainingPoints();

    // Remaining points display
    const remainingEl = document.createElement('div');
    remainingEl.className = 'attr-remaining';
    remainingEl.innerHTML = `Points Remaining: <span class="count">${remaining}</span>`;
    container.appendChild(remainingEl);

    // Attribute list
    const list = document.createElement('div');
    list.className = 'attr-list';

    for (const attr of attrs) {
      const key = attr.id;
      const baseVal = race.baseAttributes[key] || 10;
      const racialBonus = race.attributeBonuses[key] || 0;
      const playerBonus = this._bonusAttributes[key] || 0;
      const total = baseVal + racialBonus + playerBonus;

      const row = document.createElement('div');
      row.className = 'attr-row';

      // Label
      const label = document.createElement('div');
      label.className = 'attr-label';
      label.textContent = attr.abbreviation;
      row.appendChild(label);

      // Name
      const name = document.createElement('div');
      name.className = 'attr-name';
      name.textContent = attr.name;
      row.appendChild(name);

      // Minus button
      const minusBtn = document.createElement('button');
      minusBtn.className = 'attr-btn' + (playerBonus <= 0 ? ' disabled' : '');
      minusBtn.setAttribute('data-focusable', 'true');
      minusBtn.setAttribute('data-attr-minus', key);
      minusBtn.textContent = '-';
      minusBtn.addEventListener('click', () => {
        if (this._bonusAttributes[key] > 0) {
          this._bonusAttributes[key]--;
          this._renderStep(true);
        }
      });
      row.appendChild(minusBtn);

      // Value display
      const valueEl = document.createElement('div');
      valueEl.className = 'attr-value';
      valueEl.textContent = String(total);
      row.appendChild(valueEl);

      // Race bonus indicator
      const bonusEl = document.createElement('div');
      bonusEl.className = 'attr-bonus' + (racialBonus < 0 ? ' negative' : '');
      if (racialBonus !== 0) {
        bonusEl.textContent = `(${racialBonus > 0 ? '+' : ''}${racialBonus})`;
      }
      row.appendChild(bonusEl);

      // Plus button
      const plusBtn = document.createElement('button');
      plusBtn.className = 'attr-btn' + (remaining <= 0 ? ' disabled' : '');
      plusBtn.setAttribute('data-focusable', 'true');
      plusBtn.setAttribute('data-attr-plus', key);
      plusBtn.textContent = '+';
      plusBtn.addEventListener('click', () => {
        if (this._getRemainingPoints() > 0) {
          this._bonusAttributes[key]++;
          this._renderStep(true);
        }
      });
      row.appendChild(plusBtn);

      list.appendChild(row);

      // Effects tooltip
      const effects = document.createElement('div');
      effects.className = 'attr-effects';
      effects.textContent = attr.effects[0] || '';
      list.appendChild(effects);
    }

    container.appendChild(list);
  }

  _getRemainingPoints() {
    const spent = Object.values(this._bonusAttributes).reduce((s, v) => s + v, 0);
    return BONUS_POINTS - spent;
  }

  // ── Step 4: Skill Tree Selection ───────────────────────

  _renderSkillTreeStep(container) {
    const trees = dataStore.getAll('skill_trees');

    const grid = document.createElement('div');
    grid.className = 'skill-tree-grid';

    for (const tree of trees) {
      const card = document.createElement('div');
      card.className = 'skill-tree-card' +
        (this._selectedSkillTree === tree.id ? ' selected' : '');
      card.setAttribute('data-focusable', 'true');
      card.setAttribute('data-tree-id', tree.id);

      // Name
      const nameEl = document.createElement('div');
      nameEl.className = 'skill-tree-name';
      nameEl.textContent = tree.name;
      card.appendChild(nameEl);

      // Description
      const descEl = document.createElement('div');
      descEl.className = 'skill-tree-desc';
      descEl.textContent = tree.description;
      card.appendChild(descEl);

      // Solo class name
      const soloEl = document.createElement('div');
      soloEl.className = 'skill-tree-solo-class';
      soloEl.textContent = `Class: ${tree.soloClassName}`;
      card.appendChild(soloEl);

      // First 3 skill previews
      const skillsPreview = document.createElement('div');
      skillsPreview.className = 'skill-tree-skills-preview';
      const previewSkills = (tree.skills || []).slice(0, 3);
      for (const skill of previewSkills) {
        const item = document.createElement('div');
        item.className = 'skill-preview-item';
        item.innerHTML = `<span class="skill-name">${skill.name}</span> (T${skill.tier})`;
        skillsPreview.appendChild(item);
      }
      card.appendChild(skillsPreview);

      card.addEventListener('click', () => {
        this._selectedSkillTree = tree.id;
        this._renderStep(true);
      });

      grid.appendChild(card);
    }

    container.appendChild(grid);

    // Class name preview
    if (this._selectedSkillTree) {
      const className = dataStore.getClassName(this._selectedSkillTree);
      const preview = document.createElement('div');
      preview.className = 'class-preview';
      preview.innerHTML = `<div class="class-preview-label">Your Class</div><div class="class-preview-name">${className}</div>`;
      container.appendChild(preview);
    }
  }

  // ── Step 5: Scenario Selection ─────────────────────────

  _renderScenarioStep(container) {
    const list = document.createElement('div');
    list.className = 'scenario-list';

    for (const scenario of SCENARIOS) {
      const card = document.createElement('div');
      card.className = 'scenario-card' +
        (this._selectedScenario === scenario.id ? ' selected' : '');
      card.setAttribute('data-focusable', 'true');
      card.setAttribute('data-scenario-id', scenario.id);

      const titleEl = document.createElement('div');
      titleEl.className = 'scenario-title';
      titleEl.textContent = scenario.title;
      card.appendChild(titleEl);

      const descEl = document.createElement('div');
      descEl.className = 'scenario-desc';
      descEl.textContent = scenario.description;
      card.appendChild(descEl);

      const condEl = document.createElement('div');
      condEl.className = 'scenario-conditions';
      condEl.textContent = scenario.conditions;
      card.appendChild(condEl);

      card.addEventListener('click', () => {
        this._selectedScenario = scenario.id;
        this._renderStep(true);
      });

      list.appendChild(card);
    }

    container.appendChild(list);
  }

  // ── Step 6: Name Entry ─────────────────────────────────

  _renderNameStep(container) {
    const raceId = this._selectedRace?.id || 'human';

    // Generate default name if empty
    if (!this._characterName) {
      this._characterName = this._generateRandomName(raceId);
    }

    const entry = document.createElement('div');
    entry.className = 'name-entry';

    const group = document.createElement('div');
    group.className = 'name-input-group';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'name-input';
    input.id = 'chargen-name-input';
    input.placeholder = 'Enter character name...';
    input.value = this._characterName;
    input.maxLength = 24;
    input.setAttribute('data-focusable', 'true');
    input.addEventListener('input', (e) => {
      this._characterName = e.target.value;
      this._updateStatsPanel();
    });
    // Prevent keyboard nav while typing, but allow Tab and Escape to navigate away
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        // Release browser focus from input so focus manager can take over
        input.blur();
        e.preventDefault();
      } else if (e.key === 'Escape') {
        input.blur();
        e.preventDefault();
      } else if (e.key === 'Tab') {
        // Let Tab propagate to InputManager for focus navigation
        input.blur();
        // Don't stopPropagation -- let InputManager handle Tab
      } else {
        // Prevent WASD / arrow navigation while focused on input
        e.stopPropagation();
      }
    });
    group.appendChild(input);

    const randomBtn = document.createElement('button');
    randomBtn.className = 'name-random-btn';
    randomBtn.textContent = 'Random Name';
    randomBtn.setAttribute('data-focusable', 'true');
    randomBtn.addEventListener('click', () => {
      this._characterName = this._generateRandomName(raceId);
      input.value = this._characterName;
      this._updateStatsPanel();
    });
    group.appendChild(randomBtn);

    entry.appendChild(group);
    container.appendChild(entry);
  }

  _generateRandomName(raceId) {
    const bank = NAME_BANKS[raceId] || NAME_BANKS.human;
    return bank[Math.floor(Math.random() * bank.length)];
  }

  // ── Step 7: Summary & Confirm ──────────────────────────

  _renderSummaryStep(container) {
    const race = this._selectedRace || dataStore.getById('races', 'human');
    const raceId = race?.id || 'human';
    const className = this._selectedSkillTree
      ? dataStore.getClassName(this._selectedSkillTree)
      : 'Adventurer';
    const scenario = SCENARIOS.find(s => s.id === this._selectedScenario);
    const initials = (race?.name || 'H').substring(0, 2).toUpperCase();
    const portraitColor = this._selectedPortrait?.color || PORTRAIT_COLORS[raceId]?.[0] || '#444';

    const panel = document.createElement('div');
    panel.className = 'summary-panel';

    // Header with portrait + identity
    const header = document.createElement('div');
    header.className = 'summary-header';

    const portrait = document.createElement('div');
    portrait.className = 'summary-portrait';
    portrait.style.background = portraitColor;
    portrait.textContent = initials;
    header.appendChild(portrait);

    const identity = document.createElement('div');
    identity.className = 'summary-identity';
    identity.innerHTML = `
      <div class="summary-name">${this._characterName || 'Unnamed'}</div>
      <div class="summary-class">${className}</div>
      <div class="summary-race">${race?.name || 'Unknown'} ${race?.size ? `(${race.size})` : ''}</div>
    `;
    header.appendChild(identity);
    panel.appendChild(header);

    // Attributes section
    const attrSection = document.createElement('div');
    attrSection.className = 'summary-section';
    attrSection.innerHTML = `<div class="summary-section-title">Attributes</div>`;
    const attrGrid = document.createElement('div');
    attrGrid.className = 'summary-attrs';

    const attrs = ['str', 'agi', 'int', 'vit', 'wis', 'cha'];
    const attrNames = { str: 'STR', agi: 'AGI', int: 'INT', vit: 'VIT', wis: 'WIS', cha: 'CHA' };
    for (const key of attrs) {
      const base = race?.baseAttributes?.[key] || 10;
      const racial = race?.attributeBonuses?.[key] || 0;
      const bonus = this._bonusAttributes[key] || 0;
      const total = base + racial + bonus;

      const cell = document.createElement('div');
      cell.className = 'summary-attr';
      cell.innerHTML = `<div class="summary-attr-label">${attrNames[key]}</div><div class="summary-attr-value">${total}</div>`;
      attrGrid.appendChild(cell);
    }
    attrSection.appendChild(attrGrid);
    panel.appendChild(attrSection);

    // Skill tree section
    if (this._selectedSkillTree) {
      const tree = dataStore.getById('skill_trees', this._selectedSkillTree);
      const treeSection = document.createElement('div');
      treeSection.className = 'summary-section';
      treeSection.innerHTML = `
        <div class="summary-section-title">Skill Tree</div>
        <div style="color: var(--color-text);">${tree?.name || this._selectedSkillTree}</div>
        <div style="font-size: var(--text-xs); color: var(--color-text-dim);">${tree?.description || ''}</div>
      `;
      panel.appendChild(treeSection);
    }

    // Scenario section
    if (scenario) {
      const scenarioSection = document.createElement('div');
      scenarioSection.className = 'summary-section';
      scenarioSection.innerHTML = `
        <div class="summary-section-title">Starting Scenario</div>
        <div class="summary-scenario">
          <strong>${scenario.title}</strong><br/>
          ${scenario.description}
        </div>
      `;
      panel.appendChild(scenarioSection);
    }

    container.appendChild(panel);
  }

  // ── Live Stats Panel ────────────────────────────────────

  _updateStatsPanel() {
    // On the summary step (step 6), hide the stats panel since summary shows everything
    const isSummaryStep = this._currentStep === 6;

    if (this._statsSidebarEl) {
      this._statsSidebarEl.style.display = isSummaryStep ? 'none' : '';
    }
    if (this._statsMobileEl) {
      this._statsMobileEl.style.display = isSummaryStep ? 'none' : '';
    }

    if (isSummaryStep) return;

    // Build the stats panel HTML content
    const html = this._buildStatsPanelHTML();

    // Update both mobile and sidebar panels
    const sidebarPanel = document.getElementById('chargen-stats-panel-sidebar');
    if (sidebarPanel) sidebarPanel.innerHTML = html;

    const mobilePanel = document.getElementById('chargen-stats-panel-mobile');
    if (mobilePanel) mobilePanel.innerHTML = html;

    // Update mobile summary line
    this._updateMobileSummaryLine();
  }

  _buildStatsPanelHTML() {
    const race = this._selectedRace;
    const raceId = race?.id || null;
    const attrKeys = ['str', 'agi', 'int', 'vit', 'wis', 'cha'];
    const attrNames = { str: 'STR', agi: 'AGI', int: 'INT', vit: 'VIT', wis: 'WIS', cha: 'CHA' };

    let html = '<div class="stats-title">Character Build</div>';

    // Race
    if (race) {
      html += `<div class="stats-row">`;
      html += `<span class="stats-row-label">Race</span>`;
      html += `<span class="stats-row-value">${race.name}</span>`;
      html += `</div>`;

      // Racial bonuses (compact)
      const bonusParts = [];
      for (const key of attrKeys) {
        const val = race.attributeBonuses[key];
        if (val !== 0) {
          bonusParts.push(`${val > 0 ? '+' : ''}${val} ${attrNames[key]}`);
        }
      }
      if (bonusParts.length > 0) {
        html += `<div class="stats-row"><span class="stats-row-label">Bonuses</span><span class="stats-row-value" style="font-size: var(--text-xs);">${bonusParts.join(', ')}</span></div>`;
      }
    } else {
      html += `<div class="stats-placeholder">Select a race...</div>`;
    }

    // Portrait (step 2+)
    if (this._currentStep >= 1 && this._selectedPortrait) {
      const initials = this._selectedPortrait.initials || (race?.name || 'H').substring(0, 2).toUpperCase();
      html += `<div class="stats-row">`;
      html += `<span class="stats-row-label">Portrait</span>`;
      html += `<div class="stats-portrait-preview" style="background: ${this._selectedPortrait.color};">${initials}</div>`;
      html += `</div>`;
    }

    // Attributes (step 3+)
    if (this._currentStep >= 2 && race) {
      html += `<div class="stats-attrs">`;
      for (const key of attrKeys) {
        const base = race.baseAttributes[key] || 10;
        const racial = race.attributeBonuses[key] || 0;
        const bonus = this._bonusAttributes[key] || 0;
        const total = base + racial + bonus;
        html += `<div class="stats-attr"><div class="stats-attr-label">${attrNames[key]}</div><div class="stats-attr-value">${total}</div></div>`;
      }
      html += `</div>`;

      const remaining = this._getRemainingPoints();
      if (remaining > 0) {
        html += `<div class="stats-points-remaining">${remaining} pts remaining</div>`;
      }
    }

    // Skill tree (step 4+)
    if (this._currentStep >= 3 && this._selectedSkillTree) {
      const tree = dataStore.getById('skill_trees', this._selectedSkillTree);
      const className = dataStore.getClassName(this._selectedSkillTree);
      html += `<div class="stats-row"><span class="stats-row-label">Path</span><span class="stats-row-value">${tree?.name || this._selectedSkillTree}</span></div>`;
      html += `<div class="stats-row"><span class="stats-row-label">Class</span><span class="stats-row-value" style="color: var(--color-gold);">${className}</span></div>`;
    }

    // Scenario (step 5+)
    if (this._currentStep >= 4 && this._selectedScenario) {
      const scenario = SCENARIOS.find(s => s.id === this._selectedScenario);
      if (scenario) {
        html += `<div class="stats-row"><span class="stats-row-label">Story</span><span class="stats-row-value" style="font-size: var(--text-xs);">${scenario.title}</span></div>`;
      }
    }

    // Name (step 6+)
    if (this._currentStep >= 5 && this._characterName) {
      html += `<div class="stats-row"><span class="stats-row-label">Name</span><span class="stats-row-value">${this._characterName}</span></div>`;
    }

    return html;
  }

  _updateMobileSummaryLine() {
    const lineEl = this._statsMobileEl?.querySelector('.stats-summary-line');
    if (!lineEl) return;

    const parts = [];
    const attrNames = { str: 'STR', agi: 'AGI', int: 'INT', vit: 'VIT', wis: 'WIS', cha: 'CHA' };

    if (this._selectedRace) {
      parts.push(this._selectedRace.name);
    } else {
      lineEl.textContent = 'Build: Select a race...';
      return;
    }

    // Show compact attributes if on step 3+
    if (this._currentStep >= 2 && this._selectedRace) {
      const attrParts = [];
      for (const key of ['str', 'agi', 'int', 'vit', 'wis', 'cha']) {
        const base = this._selectedRace.baseAttributes[key] || 10;
        const racial = this._selectedRace.attributeBonuses[key] || 0;
        const bonus = this._bonusAttributes[key] || 0;
        const total = base + racial + bonus;
        attrParts.push(`${attrNames[key]} ${total}`);
      }
      parts.push(attrParts.join(' '));
    }

    if (this._selectedSkillTree) {
      const tree = dataStore.getById('skill_trees', this._selectedSkillTree);
      parts.push(tree?.name || this._selectedSkillTree);
    }

    lineEl.textContent = parts.join(' \u00B7 ');
  }

  // ── Navigation ─────────────────────────────────────────

  _goNext() {
    // Validate current step
    if (!this._validateStep()) return;

    if (this._currentStep < TOTAL_STEPS - 1) {
      this._currentStep++;
      this._renderStep();
    } else {
      // Final step: create character and start game
      this._createCharacter();
    }
  }

  _goBack() {
    if (this._currentStep > 0) {
      this._currentStep--;
      this._renderStep();
    } else {
      // Return to main menu
      sceneManager.transition('MainMenu');
    }
  }

  _validateStep() {
    switch (this._currentStep) {
      case 0: // Race
        if (!this._selectedRace) {
          debugLog.warn('CharCreate: No race selected');
          return false;
        }
        return true;

      case 1: // Portrait
        // Auto-select default if not picked
        if (!this._selectedPortrait) {
          const raceId = this._selectedRace?.id || 'human';
          const colors = PORTRAIT_COLORS[raceId] || PORTRAIT_COLORS.human;
          const initials = (this._selectedRace?.name || 'H').substring(0, 2).toUpperCase();
          this._selectedPortrait = { index: 0, color: colors[0], initials };
        }
        return true;

      case 2: // Attributes
        // Allow proceeding even with unspent points
        return true;

      case 3: // Skill tree
        if (!this._selectedSkillTree) {
          debugLog.warn('CharCreate: No skill tree selected');
          return false;
        }
        return true;

      case 4: // Scenario
        if (!this._selectedScenario) {
          debugLog.warn('CharCreate: No scenario selected');
          return false;
        }
        return true;

      case 5: // Name
        if (!this._characterName || !this._characterName.trim()) {
          debugLog.warn('CharCreate: No name entered');
          return false;
        }
        return true;

      case 6: // Summary - always valid
        return true;

      default:
        return true;
    }
  }

  _createCharacter() {
    const race = this._selectedRace;
    const raceId = race?.id || 'human';

    // Build final attributes
    const finalAttributes = {};
    const attrKeys = ['str', 'agi', 'int', 'vit', 'wis', 'cha'];
    for (const key of attrKeys) {
      const base = race?.baseAttributes?.[key] || 10;
      const racial = race?.attributeBonuses?.[key] || 0;
      const bonus = this._bonusAttributes[key] || 0;
      finalAttributes[key] = base + racial + bonus;
    }

    // Create character entity
    const character = new Character({
      name: this._characterName.trim(),
      raceId: raceId,
      portrait: this._selectedPortrait,
      attributes: finalAttributes,
      skillTree1: this._selectedSkillTree,
      skillTree2: null,
      scenarioId: this._selectedScenario,
    });

    // Create party with this character as leader
    const party = new Party();
    party.addMember(character);

    // Expose on debug for testing
    if (window.__debug) {
      window.__debug.party = party;
      window.__debug.character = character;
    }

    debugLog.event(`CharCreate: Character created — ${character.name}, ${character.getClassName()}, ${race?.name}`);
    debugLog.info(`CharCreate: Attributes: STR=${finalAttributes.str} AGI=${finalAttributes.agi} INT=${finalAttributes.int} VIT=${finalAttributes.vit} WIS=${finalAttributes.wis} CHA=${finalAttributes.cha}`);
    debugLog.info(`CharCreate: Scenario: ${this._selectedScenario}`);

    // Give starting consumables
    const startItems = [
      { instanceId: 'start_hp1', templateId: 'health_potion', name: 'Health Potion', slot: 'consumable', category: 'potion', consumable: true, effect: { type: 'heal', amount: 20 }, rarity: 'normal' },
      { instanceId: 'start_hp2', templateId: 'health_potion', name: 'Health Potion', slot: 'consumable', category: 'potion', consumable: true, effect: { type: 'heal', amount: 20 }, rarity: 'normal' },
      { instanceId: 'start_hp3', templateId: 'health_potion', name: 'Health Potion', slot: 'consumable', category: 'potion', consumable: true, effect: { type: 'heal', amount: 20 }, rarity: 'normal' },
    ];
    for (const item of startItems) {
      inventorySystem.addItem(item);
    }

    // Emit event for any listeners
    if (window.__debug) {
      window.__debug.characterCreated = true;
    }

    // Determine starting region from scenario
    let startRegion = 'greenhollow';
    if (this._selectedScenario === 'merchants_heir') {
      startRegion = 'dustwind_plains';
    } else if (this._selectedScenario === 'survivor') {
      startRegion = 'moonfen_marsh';
    }

    debugLog.info(`CharCreate: Adventure started! Transitioning to WorldTravel in ${startRegion}`);

    // Transition to the world travel scene
    sceneManager.transition('WorldTravel', {
      party,
      character,
      regionId: startRegion,
      scenarioId: this._selectedScenario,
    });
  }

  // ── Lifecycle ──────────────────────────────────────────

  update(dt) {
    // No per-frame updates needed
  }

  resize(w, h) {
    // CSS handles responsiveness
  }
}
